import React from 'react';
import { cn } from '../../utils/cn';

interface SkeletonLoaderProps {
  type: 'card' | 'row' | 'header' | 'title' | 'page';
  count?: number;
}

const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ type, count = 1 }) => {
  const pulseClass = "animate-pulse bg-surface-container-high rounded-xl";

  if (type === 'card') {
    return (
      <>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="bg-surface-container-low p-7 rounded-[2rem] space-y-6 h-[280px]">
            <div className="flex justify-between">
              <div className={cn(pulseClass, "w-14 h-14 rounded-2xl")} />
              <div className={cn(pulseClass, "w-20 h-6 rounded-full")} />
            </div>
            <div className="space-y-3">
              <div className={cn(pulseClass, "w-1/2 h-6")} />
              <div className={cn(pulseClass, "w-full h-4")} />
              <div className={cn(pulseClass, "w-2/3 h-4")} />
            </div>
            <div className="flex justify-between items-center pt-8">
              <div className={cn(pulseClass, "w-24 h-4")} />
              <div className={cn(pulseClass, "w-10 h-10 rounded-full")} />
            </div>
          </div>
        ))}
      </>
    );
  }

  if (type === 'row') {
    return (
      <>
        {Array.from({ length: count }).map((_, i) => (
          <tr key={i} className="border-b border-outline-variant/10">
            <td className="py-6 pl-6 pr-4"><div className={cn(pulseClass, "w-4 h-4 rounded")} /></td>
            <td className="py-6 px-4">
              <div className="flex items-center gap-3">
                <div className={cn(pulseClass, "w-9 h-9")} />
                <div className={cn(pulseClass, "w-32 h-4")} />
              </div>
            </td>
            <td className="py-6 px-4"><div className={cn(pulseClass, "w-24 h-4")} /></td>
            <td className="py-6 px-4"><div className={cn(pulseClass, "w-16 h-4")} /></td>
            <td className="py-6 pl-4 pr-6 text-right"><div className={cn(pulseClass, "inline-block w-8 h-8 ml-auto")} /></td>
          </tr>
        ))}
      </>
    );
  }

  if (type === 'page') {
    return (
      <div className="space-y-12 animate-fade-in">
        {/* Header Skeleton */}
        <div className="flex justify-between items-end">
          <div className="space-y-3">
            <div className={cn(pulseClass, "w-24 h-3")} />
            <div className={cn(pulseClass, "w-96 h-12 md:h-16")} />
            <div className={cn(pulseClass, "w-64 h-5")} />
          </div>
          <div className="flex gap-4">
            <div className={cn(pulseClass, "w-12 h-12 rounded-2xl")} />
            <div className={cn(pulseClass, "w-12 h-12 rounded-2xl")} />
            <div className={cn(pulseClass, "w-24 h-12 rounded-2xl ml-4")} />
          </div>
        </div>

        {/* Grid Skeleton */}
        <div className="space-y-8">
          <div className={cn(pulseClass, "w-48 h-8")} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <SkeletonLoader type="card" count={4} />
          </div>
        </div>

        {/* Table Skeleton */}
        <div className="space-y-6">
          <div className={cn(pulseClass, "w-48 h-8")} />
          <div className="bg-surface-container-lowest rounded-[2rem] shadow-ambient overflow-hidden border border-outline-variant/5">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-surface-container-low/30">
                  <th className="py-4 pl-6 pr-4 w-12"><div className={cn(pulseClass, "w-4 h-4 rounded")} /></th>
                  <th className="py-4 px-4"><div className={cn(pulseClass, "w-24 h-3")} /></th>
                  <th className="py-4 px-4"><div className={cn(pulseClass, "w-24 h-3")} /></th>
                  <th className="py-4 px-4"><div className={cn(pulseClass, "w-16 h-3")} /></th>
                  <th className="py-4 pl-4 pr-6 text-right"><div className={cn(pulseClass, "w-8 h-3 inline-block")} /></th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-outline-variant/10">
                    <td className="py-6 pl-6 pr-4"><div className={cn(pulseClass, "w-4 h-4 rounded")} /></td>
                    <td className="py-6 px-4">
                      <div className="flex items-center gap-3">
                        <div className={cn(pulseClass, "w-9 h-9")} />
                        <div className={cn(pulseClass, "w-32 h-4")} />
                      </div>
                    </td>
                    <td className="py-6 px-4"><div className={cn(pulseClass, "w-24 h-4")} /></td>
                    <td className="py-6 px-4"><div className={cn(pulseClass, "w-16 h-4")} /></td>
                    <td className="py-6 pl-4 pr-6 text-right"><div className={cn(pulseClass, "inline-block w-8 h-8 ml-auto")} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SkeletonLoader;
